import React, { Dispatch, SetStateAction, useEffect, useState } from "react"
import { DateTime } from "luxon"
import { DatePicker } from "@mui/x-date-pickers"
import { FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Switch } from "@mui/material"
import { CustomFilterMenuAccordion, FilterMenu } from "../../../../../../shared/components/filters/FilterMenu"
import { makeStyles } from "tss-react/mui"
import { MerchantSatelliteFilter } from "../../components/inputFilters/MerchantSatelliteFilter"
import { MerchantBillReceiversFilter } from "../../components/inputFilters/MerchantBillReceiversFilter"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import useActiveFiltersCount from "../hooks/useActiveFiltersCount"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveUserType } from "../../../../../context/contextSelectors"
import { UserType } from "../../../../../login/loginTypes"
import useFilterAccordions from "../../../../../../shared/components/filters/hooks/useFilterAccordions"
import { useDebouncedEffect } from "../../../../../../shared/hooks/useDebouncedEffect"
import { MultiMerchantSatellitesFilter } from "../../components/inputFilters/MultiMerchantSatellitesFilter"
import { StandardCustomerSelect } from "../../../../../../shared/components/filters/components/StandardCustomerSelect"
import { StandardEmailFilter } from "../../../../../../shared/components/filters/components/StandardEmailFilter"
import { StandardIdFilter } from "../../../../../../shared/components/filters/components/StandardIdFilter"
import { StandardLast4NumbersFilter } from "../../../../../../shared/components/filters/components/StandardLast4NumbersFilter"
import { StandardPaymentMethodsFilter } from "../../../../../../shared/components/filters/components/StandardPaymentMethodsFilter"
import { StandardMerchantsMultiselectFilter } from "../../../../../../shared/components/filters/components/StandardMerchantsMultiselectFilter"
import { StandardMerchantShopsFilter } from "../../../../../../shared/components/filters/components/StandardMerchantShopsFilter"
import { StandardMultiMerchantShopsFilter } from "../../../../../../shared/components/filters/components/StandardMultiMerchantShopsFilter"
import { StandardAlertInfo } from "../../../../../../shared/components/alert/StandardAlertInfo"

export type MeetingServiceFilterProps = Readonly<
    Partial<{
        orderId?: string
        pendingApproval: boolean
        invoiceStatus: string
        isBooked: string
        startBookedDate: DateTime | null
        endBookedDate: DateTime | null
        startDeliveryDate: DateTime | null
        endDeliveryDate: DateTime | null
        merchantId: string
        merchantIds: string[]
        shopIds: string[]
        satelliteIds: string[]
        customerId: string
        userGroupId: string
        email: string
        employeeId: string
        buyerPartyIds: string[]
        paymentMethod: string[]
        creditCardLast4: string
    }>
>

export const defaultMeetingServiceFilterOptionsList: MeetingServiceFilterProps = {
    orderId: undefined,
    pendingApproval: false,
    invoiceStatus: "ALL",
    isBooked: "ALL",
    startBookedDate: null,
    endBookedDate: null,
    startDeliveryDate: null,
    endDeliveryDate: null,
    merchantId: "",
    merchantIds: undefined,
    shopIds: undefined,
    satelliteIds: undefined,
    customerId: undefined,
    userGroupId: undefined,
    employeeId: undefined,
    email: undefined,
    buyerPartyIds: undefined,
    paymentMethod: undefined,
    creditCardLast4: undefined,
}

type DisabledFilters = {
    disablePendingApproval?: boolean
    disableInvoicing?: boolean
    disableDeliveryDate?: boolean
    disableMerchantShops?: boolean
    disableServicePayment?: boolean
}

type FiltersDrawerContentMeetingServiceProps = {
    drawerOpen: boolean
    setDrawerOpen: (val: boolean) => void
    setFiltersList: Dispatch<SetStateAction<MeetingServiceFilterProps>>
    filtersList: MeetingServiceFilterProps
    disabledFilters?: DisabledFilters
    tab?: "accounting-meeting-service" | string
}

const useStyles = makeStyles()(() => ({
    input: {
        width: "20rem",
    },
}))

type FilterAccordions = {
    order: boolean
    period: boolean
    invoicing: boolean
    deliveryDate: boolean
    merchant: boolean
    debtor: boolean
    endUser: boolean
    payment: boolean
}

export function FiltersDrawerMeetingService({
    drawerOpen,
    setDrawerOpen,
    setFiltersList,
    filtersList,
    disabledFilters,
    tab,
}: FiltersDrawerContentMeetingServiceProps) {
    const { classes } = useStyles()
    const [optionsList, setOptionsList] = useState<MeetingServiceFilterProps>(filtersList)

    const { expandedAccordions, toggleAccordion } = useFilterAccordions<FilterAccordions>({
        order: false,
        period: false,
        invoicing: false,
        deliveryDate: false,
        merchant: false,
        debtor: false,
        endUser: false,
        payment: false,
    })
    const currentUserType = useAppSelector(selectEffectiveUserType)

    const {
        merchantActive,
        orderActive,
        invoiceActive,
        deliveryDateActive,
        paymentActive,
        debtorActive,
        endUserActive,
    } = useActiveFiltersCount(optionsList, disabledFilters)

    useEffect(() => {
        setOptionsList(filtersList)
    }, [filtersList])

    useDebouncedEffect(() => {
        setFiltersList(optionsList)
    }, [optionsList, setFiltersList])

    const handleClose = () => {
        setDrawerOpen(false)
    }

    const handleMerchantClear = () => {
        setOptionsList((prevOptions) => {
            return {
                ...prevOptions,
                merchantId: currentUserType === UserType.MERCHANT ? optionsList.merchantId : "",
                customerId: "",
                buyerPartyIds: [],
                merchantIds: [],
                shopIds: [],
                satelliteIds: [],
            }
        })
    }

    const handleClearAll = () => {
        setOptionsList({
            ...defaultMeetingServiceFilterOptionsList,
            merchantId: currentUserType === UserType.MERCHANT ? optionsList.merchantId : "",
        })
    }

    const handelClearByType = (type: keyof MeetingServiceFilterProps) => {
        setOptionsList((prevOptions) => {
            return {
                ...prevOptions,
                [type]: defaultMeetingServiceFilterOptionsList[type],
            }
        })
    }

    return (
        <FilterMenu
            drawerOpen={drawerOpen}
            handleClose={handleClose}
            handleClearAll={handleClearAll}
            isShowClearAllButton={true}
        >
            <LocalizedStrict id="supplier-meeting-service-order-title" attrs={{ title: true }}>
                <CustomFilterMenuAccordion
                    expanded={expandedAccordions.order}
                    toggleAccordion={() => toggleAccordion("order")}
                    title="Order"
                    handleClear={() => {
                        handelClearByType("orderId")
                        handelClearByType("pendingApproval")
                    }}
                    counter={orderActive}
                >
                    <Grid container direction="column" gap="1rem">
                        <StandardIdFilter
                            value={optionsList.orderId}
                            onChange={(orderId) =>
                                setOptionsList((prev) => ({
                                    ...prev,
                                    orderId,
                                }))
                            }
                            type="orderId"
                        />
                        {currentUserType === UserType.MERCHANT && !disabledFilters?.disablePendingApproval && (
                            <LocalizedStrict
                                id="supplier-meeting-service-drawer-pending-approval-label"
                                attrs={{ label: true }}
                            >
                                <FormControlLabel
                                    label="Pending approval"
                                    control={
                                        <Switch
                                            checked={optionsList.pendingApproval}
                                            onChange={(_, state) =>
                                                setOptionsList((prev) => ({
                                                    ...prev,
                                                    pendingApproval: state,
                                                }))
                                            }
                                        />
                                    }
                                />
                            </LocalizedStrict>
                        )}
                    </Grid>
                </CustomFilterMenuAccordion>
            </LocalizedStrict>
            {!disabledFilters?.disableInvoicing && (
                <LocalizedStrict id="supplier-meeting-service-invoicing-title" attrs={{ title: true }}>
                    <CustomFilterMenuAccordion
                        expanded={expandedAccordions.invoicing}
                        toggleAccordion={() => toggleAccordion("invoicing")}
                        title="Invoicing"
                        handleClear={() => {
                            handelClearByType("invoiceStatus")
                            handelClearByType("isBooked")
                        }}
                        counter={invoiceActive}
                    >
                        <Grid container direction="column" gap="1rem">
                            <FormControl fullWidth>
                                <LocalizedStrict id="supplier-meeting-service-drawer-invoice-status-label">
                                    <InputLabel id="supplier-meeting-service-drawer-invoice-status-label">
                                        Invoice status
                                    </InputLabel>
                                </LocalizedStrict>
                                <Select
                                    className={classes.input}
                                    labelId="supplier-meeting-service-drawer-invoice-status-label"
                                    id="supplier-meeting-service-drawer-invoice-status"
                                    value={optionsList.invoiceStatus}
                                    label="Invoice status"
                                    onChange={(e) =>
                                        setOptionsList((prev) => ({
                                            ...prev,
                                            invoiceStatus: e.target.value,
                                        }))
                                    }
                                >
                                    <MenuItem value="ALL">
                                        <LocalizedStrict id="supplier-meeting-service-drawer-invoice-status-all-label">
                                            <>All</>
                                        </LocalizedStrict>
                                    </MenuItem>
                                    <MenuItem value="SENT">
                                        <LocalizedStrict id="supplier-meeting-service-drawer-invoice-status-sent-label">
                                            <>Sent</>
                                        </LocalizedStrict>
                                    </MenuItem>
                                    <MenuItem value="NOT_SENT">
                                        <LocalizedStrict id="supplier-meeting-service-drawer-invoice-status-not-sent-label">
                                            <> Not sent</>
                                        </LocalizedStrict>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            {currentUserType === UserType.MERCHANT && (
                                <FormControl fullWidth>
                                    <LocalizedStrict id="supplier-meeting-service-drawer-booked-label">
                                        <InputLabel id="supplier-meeting-service-drawer-booked-label">
                                            Booked
                                        </InputLabel>
                                    </LocalizedStrict>
                                    <Select
                                        className={classes.input}
                                        labelId="supplier-meeting-service-drawer-booked-label"
                                        id="supplier-meeting-service-drawer-booked"
                                        value={optionsList.isBooked}
                                        label="Booked"
                                        onChange={(e) =>
                                            setOptionsList((prev) => ({
                                                ...prev,
                                                isBooked: e.target.value,
                                            }))
                                        }
                                    >
                                        <MenuItem value="ALL">
                                            <LocalizedStrict id="supplier-meeting-service-drawer-booked-all-label">
                                                <>All</>
                                            </LocalizedStrict>
                                        </MenuItem>
                                        <MenuItem value="BOOKED">
                                            <LocalizedStrict id="supplier-meeting-service-drawer-booked-booked-label">
                                                <>Booked</>
                                            </LocalizedStrict>
                                        </MenuItem>
                                        <MenuItem value="NOT_BOOKED">
                                            <LocalizedStrict id="supplier-meeting-service-drawer-booked-not-booked-label">
                                                <>Not booked</>
                                            </LocalizedStrict>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                        </Grid>
                    </CustomFilterMenuAccordion>
                </LocalizedStrict>
            )}
            {!disabledFilters?.disableDeliveryDate && (
                <LocalizedStrict id="supplier-meeting-service-delivery-date-title" attrs={{ title: true }}>
                    <CustomFilterMenuAccordion
                        title="Delivery date"
                        expanded={expandedAccordions.deliveryDate}
                        toggleAccordion={() => toggleAccordion("deliveryDate")}
                        handleClear={() => {
                            handelClearByType("startDeliveryDate")
                            handelClearByType("endDeliveryDate")
                        }}
                        counter={deliveryDateActive}
                    >
                        <Grid container direction="column" gap="1rem">
                            <LocalizedStrict
                                id="supplier-meeting-service-drawer-from-date-label"
                                attrs={{ label: true }}
                            >
                                <DatePicker
                                    className={classes.input}
                                    value={optionsList.startDeliveryDate}
                                    label="From date"
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            setOptionsList((prev) => ({
                                                ...prev,
                                                startDeliveryDate: newValue,
                                            }))
                                        }
                                    }}
                                />
                            </LocalizedStrict>
                            <LocalizedStrict id="supplier-meeting-service-drawer-to-date-label" attrs={{ label: true }}>
                                <DatePicker
                                    className={classes.input}
                                    value={optionsList.endDeliveryDate}
                                    label="To date"
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            setOptionsList((prev) => ({
                                                ...prev,
                                                endDeliveryDate: newValue,
                                            }))
                                        }
                                    }}
                                />
                            </LocalizedStrict>
                            {tab === "accounting-meeting-service" && (
                                <LocalizedStrict
                                    id="supplier-meeting-service-drawer-to-date-info-label"
                                    attrs={{ title: true }}
                                >
                                    <StandardAlertInfo title="Do not use delivery date for invoicing. Use booking date instead." />
                                </LocalizedStrict>
                            )}
                        </Grid>
                    </CustomFilterMenuAccordion>
                </LocalizedStrict>
            )}
            <LocalizedStrict id="supplier-meeting-service-merchant-title" attrs={{ title: true }}>
                <CustomFilterMenuAccordion
                    title="Merchant"
                    expanded={expandedAccordions.merchant}
                    toggleAccordion={() => toggleAccordion("merchant")}
                    handleClear={handleMerchantClear}
                    counter={merchantActive}
                >
                    <Grid container direction="column" gap="1rem">
                        {currentUserType !== UserType.MERCHANT && (
                            <StandardMerchantsMultiselectFilter
                                value={optionsList.merchantIds || []}
                                setValue={(merchantIds) => {
                                    setOptionsList((prev) => ({
                                        ...prev,
                                        merchantIds: merchantIds,
                                    }))
                                }}
                            />
                        )}
                        {!disabledFilters?.disableMerchantShops &&
                            (currentUserType === UserType.MERCHANT ? (
                                <StandardMerchantShopsFilter
                                    merchantId={optionsList.merchantId}
                                    value={optionsList.shopIds || []}
                                    setValue={(shopIds) => {
                                        setOptionsList((prev) => ({
                                            ...prev,
                                            shopIds,
                                        }))
                                    }}
                                />
                            ) : (
                                <StandardMultiMerchantShopsFilter
                                    merchantIds={optionsList.merchantIds || []}
                                    selectedIds={optionsList.shopIds || []}
                                    setSelectedIds={(shopIds) => {
                                        setOptionsList((prev) => ({
                                            ...prev,
                                            shopIds,
                                        }))
                                    }}
                                />
                            ))}

                        {currentUserType === UserType.MERCHANT ? (
                            <MerchantSatelliteFilter
                                merchantId={optionsList.merchantId}
                                value={optionsList.satelliteIds || []}
                                setValue={(satelliteIds) => {
                                    setOptionsList((prev) => ({
                                        ...prev,
                                        satelliteIds,
                                    }))
                                }}
                            />
                        ) : (
                            <MultiMerchantSatellitesFilter
                                merchantIds={optionsList.merchantIds || []}
                                selectedIds={optionsList.satelliteIds || []}
                                setSelectedIds={(satelliteIds) => {
                                    setOptionsList((prev) => ({
                                        ...prev,
                                        satelliteIds,
                                    }))
                                }}
                            />
                        )}
                    </Grid>
                </CustomFilterMenuAccordion>
            </LocalizedStrict>

            <LocalizedStrict id="supplier-meeting-service-debtor-title" attrs={{ title: true }}>
                <CustomFilterMenuAccordion
                    title="Debtor"
                    expanded={expandedAccordions.debtor}
                    toggleAccordion={() => toggleAccordion("debtor")}
                    handleClear={() => {
                        handelClearByType("customerId")
                        handelClearByType("userGroupId")
                        handelClearByType("buyerPartyIds")
                    }}
                    counter={debtorActive}
                >
                    <Grid container direction="column" gap="1rem">
                        <StandardCustomerSelect
                            setCustomerId={(customerId) =>
                                setOptionsList((prev) => ({
                                    ...prev,
                                    customerId,
                                    buyerPartyIds: undefined,
                                }))
                            }
                            setCustomerGroupId={(userGroupId) =>
                                setOptionsList((prev) => ({
                                    ...prev,
                                    userGroupId,
                                }))
                            }
                            customerId={optionsList.customerId}
                            customerGroupId={optionsList.userGroupId}
                            merchantIdValue={optionsList.merchantId}
                            isFullWidth
                            isFilterDrawer
                        />
                        {currentUserType === UserType.MERCHANT && (
                            <MerchantBillReceiversFilter
                                merchantId={optionsList.merchantId}
                                customerId={optionsList.customerId}
                                value={optionsList.buyerPartyIds || []}
                                setValue={(buyerPartyIds) => {
                                    setOptionsList((prev) => ({
                                        ...prev,
                                        buyerPartyIds,
                                    }))
                                }}
                            />
                        )}
                    </Grid>
                </CustomFilterMenuAccordion>
            </LocalizedStrict>

            <LocalizedStrict id="supplier-meeting-service-end-user-title" attrs={{ title: true }}>
                <CustomFilterMenuAccordion
                    title="End user"
                    expanded={expandedAccordions.endUser}
                    toggleAccordion={() => toggleAccordion("endUser")}
                    handleClear={() => {
                        handelClearByType("employeeId")
                        handelClearByType("email")
                    }}
                    counter={endUserActive}
                >
                    <Grid container direction="column" gap="1rem">
                        <StandardEmailFilter
                            value={optionsList.email}
                            onChange={(email) => {
                                setOptionsList((prev) => ({
                                    ...prev,
                                    email,
                                }))
                            }}
                        />
                        <StandardIdFilter
                            value={optionsList.employeeId}
                            onChange={(employeeId) => {
                                setOptionsList((prev) => ({
                                    ...prev,
                                    employeeId,
                                }))
                            }}
                            type="employeeId"
                        />
                    </Grid>
                </CustomFilterMenuAccordion>
            </LocalizedStrict>
            {!disabledFilters?.disableServicePayment && (
                <LocalizedStrict id="supplier-meeting-service-payment-title" attrs={{ title: true }}>
                    <CustomFilterMenuAccordion
                        title="Payment"
                        expanded={expandedAccordions.payment}
                        toggleAccordion={() => toggleAccordion("payment")}
                        handleClear={() => {
                            handelClearByType("paymentMethod")
                            handelClearByType("creditCardLast4")
                        }}
                        counter={paymentActive}
                    >
                        <Grid container direction="column" gap="1rem">
                            <StandardPaymentMethodsFilter
                                value={optionsList.paymentMethod || []}
                                setValue={(paymentMethod) =>
                                    setOptionsList((prev) => ({
                                        ...prev,
                                        paymentMethod,
                                    }))
                                }
                            />
                            <StandardLast4NumbersFilter
                                value={optionsList.creditCardLast4}
                                onChange={(creditCardLast4) => {
                                    setOptionsList((prev) => ({
                                        ...prev,
                                        creditCardLast4,
                                    }))
                                }}
                            />
                        </Grid>
                    </CustomFilterMenuAccordion>
                </LocalizedStrict>
            )}
        </FilterMenu>
    )
}
